import apiClient from "services/apiClient";

export const GetBlockNumber = async (
  api,
  UserData,
  authToken,
  formatNumber,
  setBlockNumbers
) => {
  try {
    const response = await apiClient.get(
      `${api}get-block-numbers/${UserData.id}`,
      {
        headers: {
          Authorization: "Bearer " + authToken,
        },
      }
    );
    const serverBlocked = response.data.serverBlocked;
    const userBlocked = response.data.userBlocked;
    const serverNumbers = serverBlocked
      ?.map((item) => formatNumber(item.numbers))
      .filter(Boolean) || [];
    const userNumbers = userBlocked
      ?.map((item) => formatNumber(item.numbers))
      .filter(Boolean) || [];
    const total = [...serverNumbers, ...userNumbers];
    setBlockNumbers([...new Set(total)]);
  } catch (error) {
    console.log(error);
  }
};
export const normalizePhoneNumber = (value, international = false) => {
  if (value === null || value === undefined) return null;

  const digits = String(value).trim().replace(/\D/g, "");
  if (!digits) return null;

  if (international) {
    const length = digits.length;
    return length >= 8 && length <= 15 ? digits : null;
  }

  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12) return digits;

  return null;
};

export const buildValidateCampaignPayload = ({
  userId,
  campaignType,
  templateName,
  customText,
  filteredNumbers,
  templateCategory,
  isInternational,
  sourceCampaignId,
  statuses,
}) => ({
  user_id: userId,
  campaign_type: campaignType,
  template_name: templateName,
  custom_text: customText,
  numbers_count: filteredNumbers.length,
  mobile_numbers: filteredNumbers.map((number) => String(number)),
  template_category: templateCategory,
  international: isInternational,
  ...(sourceCampaignId && statuses?.length
    ? { source_campaign_id: sourceCampaignId, statuses }
    : {}),
});

export const getValidateCampaignResult = (response) => {
  const body = response?.data?.data ?? response?.data ?? {};

  return {
    canProceed: body.can_proceed === true || (body.can_proceed !== false && body.valid === true),
    balanceRequired: Number(body.balance_required),
    currentBalance: Number(body.current_balance),
    message: body.message || body.error || null,
    gatewayEnabled: body.gateway_enabled,
    countrySummary: body.country_summary,
    missingCountries: body.missing_countries,
    inactiveGatewayGroups: body.inactive_gateway_groups || [],
  };
};

export const collectInactiveGatewayNumbers = (groups = []) =>
  (groups || []).flatMap((group) => group.numbers || []);

export const handleCampaignValidationFailure = (validation, {
  setError,
  showBalanceModal,
  setInactiveGatewayModal,
  setInactiveGatewayGroups,
  setInactiveGatewayMessage,
  scrollToTop,
}) => {
  if (
    Number.isFinite(validation.balanceRequired) &&
    Number.isFinite(validation.currentBalance) &&
    validation.currentBalance < validation.balanceRequired
  ) {
    showBalanceModal?.();
  }

  if (validation.inactiveGatewayGroups?.length > 0) {
    setInactiveGatewayGroups?.(validation.inactiveGatewayGroups);
    setInactiveGatewayMessage?.(validation.message || "Gateway not active");
    setInactiveGatewayModal?.(true);
    setError(validation.message || "Gateway not active");
    scrollToTop?.();
    return true;
  }

  setError(validation.message || "Campaign validation failed");
  scrollToTop?.();
  return false;
};

export const handleRemoveInactiveGatewayNumbers = ({
  inactiveGatewayGroups,
  numbers,
  groupNumbers,
  number,
  SetNumbers,
  setGroupNumbers,
  setNumber,
  importedData,
  setImportedData,
  onClose,
  international = false,
}) => {
  const numbersToRemove = collectInactiveGatewayNumbers(inactiveGatewayGroups);
  const removeSet = new Set(
    numbersToRemove
      .map((value) => normalizePhoneNumber(value, international))
      .filter(Boolean)
  );

  const keepNumber = (value) => {
    const normalized = normalizePhoneNumber(value, international);
    return normalized && !removeSet.has(normalized);
  };

  if (Array.isArray(numbers)) {
    SetNumbers(numbers.filter(keepNumber));
  }

  if (setGroupNumbers && Array.isArray(groupNumbers)) {
    setGroupNumbers(groupNumbers.filter(keepNumber));
  }

  if (number && String(number).trim()) {
    const keptInputNumbers = processPhoneNumbers(number, { international }).filter(keepNumber);
    setNumber(keptInputNumbers.join(", "));
  }

  if (setImportedData && Array.isArray(importedData)) {
    setImportedData(
      importedData.filter((row) => keepNumber(row?.number ?? row))
    );
  }

  onClose?.();
};

export const getApiErrorMessage = (error, fallback = 'Something went wrong') => (
  error?.response?.data?.data?.message
  || error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallback
);

export const validateInputs = (
  requestType,
  templateName,
  template,
  mediaLink,
  templateInputs,
  dynamicValues,
  templateUrlInputs,
  dynamicButtonUrl,
  location = {}
) => {
  if (
    !requestType ||
    (requestType === "template" &&
      (!templateName ||
        templateName === "" ||
        templateName === "choose..." ||
        templateName.trim() === ""))
  ) {
    return "You have to select a template first";
  } else if (
    template.header &&
    template.header.format !== "TEXT" &&
    template.header.format !== "LOCATION" &&
    !mediaLink
  ) {
    return "Please Select Media Before Submitting";
  } else if (template.header && template.header.format === "LOCATION") {
    // console.log('Location validation - received location:', location);
    // console.log('Location fields:', {
    //     latitude: location.latitude,
    //     longitude: location.longitude,
    //     name: location.name,
    //     address: location.address
    // });

    // Validate location fields - check for empty strings and whitespace
    const latitudeStr = location.latitude?.toString().trim();
    const longitudeStr = location.longitude?.toString().trim();
    const locationName = location.name?.toString().trim();
    const locationAddress = location.address?.toString().trim();
    if (!latitudeStr || !longitudeStr || !locationName || !locationAddress) {
      // console.log(latitudeStr, longitudeStr, locationName, locationAddress);
      // console.log('Missing location fields detected');
      return "Please fill in all location fields (latitude, longitude, name, and address)";
    }

    // Validate latitude range (-90 to 90)
    const latitudeNum = parseFloat(location.latitude);
    if (isNaN(latitudeNum) || latitudeNum < -90 || latitudeNum > 90) {
      return "Latitude must be a valid number between -90 and 90";
    }

    // Validate longitude range (-180 to 180)
    const longitudeNum = parseFloat(location.longitude);
    if (isNaN(longitudeNum) || longitudeNum < -180 || longitudeNum > 180) {
      return "Longitude must be a valid number between -180 and 180";
    }
  } else if (
    requestType === "template" &&
    templateInputs &&
    templateInputs?.length > 0
  ) {
    const missingInputs = dynamicValues.length < templateInputs.length;
    if (missingInputs) {
      //console.log('error',  `Please fill in the template variable fields`);
      return `Please fill in the template variable fields`;
    }
  }
  if (
    requestType === "template" &&
    templateUrlInputs &&
    templateUrlInputs.length > 0
  ) {
    const missingUrls = dynamicButtonUrl.length < templateUrlInputs.length;
    if (missingUrls) {
      return `Please fill in the required URL fields: ${templateUrlInputs
        .map((input) => input.name)
        .join(", ")}`;
    }
  }

  return null;
};

export const validateCampaignInputs = ({
  setError,
  scrollToTop,
  numbers,
  number,
  blockNumbers,
  setBlockedNumbersSample,
  setBlockedNumbersModal,
  requestType,
  templateName,
  template,
  mediaLink,
  templateInputs,
  dynamicValues,
  templateUrlInputs,
  dynamicButtonUrl,
  location = {},
  international = false,
  allowEmptyNumbers = false,
}) => {
  // console.log('dynamicValues', dynamicValues);
  const errorMessage = validateInputs(
    requestType,
    templateName,
    template,
    mediaLink,
    templateInputs,
    dynamicValues,
    templateUrlInputs,
    dynamicButtonUrl,
    location
  );
  if (errorMessage) {
    setError(errorMessage);
    scrollToTop();
    return null;
  }

  const groupNumbers = Array.isArray(numbers)
    ? numbers.map((item) => normalizePhoneNumber(item, international)).filter(Boolean)
    : [];

  const inputNumbers =
    number && number.trim()
      ? processPhoneNumbers(number, { international })
      : [];

  const allNumbers = Array.from(new Set([...groupNumbers, ...inputNumbers]));

  const blocked = Array.isArray(blockNumbers)
    ? blockNumbers.map((item) => normalizePhoneNumber(item, international)).filter(Boolean)
    : [];
  const blockedSet = new Set(blocked);

  const intersectingNumbers = allNumbers.filter((num) => blockedSet.has(num));
  if (intersectingNumbers.length > 0) {
    const sampleNumbers = intersectingNumbers.slice(0, 5);
    setBlockedNumbersSample(sampleNumbers);
    setBlockedNumbersModal(true);
    return null;
  }

  const filteredNumbers = allNumbers.filter((num) => !blockedSet.has(num));
  if (filteredNumbers?.length === 0 && !allowEmptyNumbers) {
    setError("No valid numbers to process");
    scrollToTop();
    return null;
  }

  if (filteredNumbers?.length > 10000) {
    setError("You can only process up to 10000 numbers.");
    scrollToTop();
    return null;
  }

  return {
    allNumbers,
    filteredNumbers,
  };
};

export const processPhoneNumbers = (numberString, options = {}) => {
  if (!numberString || !numberString.trim()) {
    return options.existingNumbers || [];
  }

  const {
    filterBlocked = false,
    blockedNumbers = [],
    unique = false,
    existingNumbers = [],
    international = false,
  } = options;

  let processedNumbers = numberString
    .split(",")
    .map((item) => item.trim())
    .flatMap((item) => item.split("\n"))
    .map((num) => normalizePhoneNumber(num, international))
    .filter(Boolean);

  // Filter out blocked numbers if requested
  if (filterBlocked && blockedNumbers.length > 0) {
    const blockedSet = new Set(
      blockedNumbers
        .map((item) => normalizePhoneNumber(item, international))
        .filter(Boolean)
    );
    processedNumbers = processedNumbers.filter((num) => !blockedSet.has(num));
  }

  // Combine with existing numbers if provided
  if (existingNumbers.length > 0) {
    return unique
      ? [...new Set([...existingNumbers, ...processedNumbers])]
      : [...existingNumbers, ...processedNumbers];
  }

  return processedNumbers;
};

export const handleRemoveBlockedNumbers = (
  numbers,
  number,
  blockNumbers,
  SetNumbers,
  setNumber,
  setBlockedNumbersModal,
  international = false
) => {
  const blocked = Array.isArray(blockNumbers)
    ? blockNumbers
        .map((item) => normalizePhoneNumber(item, international))
        .filter(Boolean)
    : [];
  const blockedSet = new Set(blocked);

  const normalizedGroup = Array.isArray(numbers)
    ? numbers
        .map((item) => normalizePhoneNumber(item, international))
        .filter(Boolean)
    : [];
  const filteredGroup = normalizedGroup.filter((num) => !blockedSet.has(num));
  SetNumbers(filteredGroup);

  if (number && number?.trim()) {
    const inputNumbersArray = processPhoneNumbers(number, {
      filterBlocked: true,
      blockedNumbers: blocked,
      international,
    });
    setNumber(inputNumbersArray.join(", "));
  }
  setBlockedNumbersModal(false);
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scrolling
  });
};
